/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { css, useTheme } from '@emotion/react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  Button,
  Text,
} from "@coreai/component-library";
import { useLanguage } from "LanguageContext";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { useAnalysis } from "../Analyses/FormComponents/AnalysisResultContext";

interface DeleteRewriteDialogProps {
  item: any;
  open: boolean;
  onClose: () => void;
  loader: boolean;
  onDelete: (selectedItems: string[]) => void;
}

const styles = {
    checkboxGroup: css`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
  `,

  checkboxItem: (checked: boolean, color: string) => css`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.8rem;
    color: ${checked ? '#000' : '#607A8C'};
    font-weight: ${checked ? 600 : 400};
    cursor: pointer;
    input {
      margin-top: 5px;
      accent-color: ${color};
    }
    p {
      margin-top: 0px;
    }
  `,
};

export default function DeleteRewriteDialog(
  props: Readonly<DeleteRewriteDialogProps>
) {
  const { item, loader } = props;
  const { t } = useLanguage();
  const theme = useTheme();
  const {analysisContextResult} = useAnalysis();
  const [selectedChecks, setSelectedChecks] = useState<string[]>([]);

  const handleCheckboxChange = (id: string) => {
    setSelectedChecks((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  React.useEffect(() => {
    if (item?.length === 1) {
      setSelectedChecks([item[0].id]);
    }
  }, [item]);

  const alertDialogBodyStyles = {
    // border: "none",
  } as React.CSSProperties;

  const bodyStyles = {
    fontFamily: theme.typography.bodyMedium.fontFamily,
    fontSize: theme.typography.bodyMedium.fontSize,
    fontWeight: theme.typography.bodyMedium.fontWeight,
    lineHeight: theme.typography.bodyMedium.lineHeight,
    color: theme.colors.textDark,
    marginBottom: theme.spacing.sm,
    paddingTop: 0,
    // overflowY: "visible",
    gap: "0.5rem",
  } as React.CSSProperties;

  const titleHighlight = css`
    color: ${theme.colors.primary};
    margin-top: 0px;
    font-size: 12px;
  `;

  return (
    <AlertDialog
      open={props.open}
      onOpenChange={(isOpen) => !isOpen && props.onClose()}
    >
      <AlertDialogContent size="lg">
        <AlertDialogHeader style={alertDialogBodyStyles}>
          <AlertDialogTitle>
            {t("rephrased_Delete_header", null, analysisContextResult?.locale_code)}
          </AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogBody style={bodyStyles}>
          <Text style={bodyStyles}>
            {t("rephrased_delete_title", null, analysisContextResult?.locale_code)}
          </Text>
            
          {item?.length > 1 && (<p css={titleHighlight}>{t("rephrased_delete_select_item", null, analysisContextResult?.locale_code)}</p>)}

          <div css={styles.checkboxGroup}>
            {item && item?.map((opt: any) => (
              <label key={opt.id} css={styles.checkboxItem(selectedChecks.includes(opt.id), theme.colors.primary)}>
                {item?.length > 1 && (
                  <input
                    type="checkbox"
                    checked={selectedChecks.includes(opt.id)}
                    onChange={() => handleCheckboxChange(opt.id)}
                  />
                )}
                <Markdown rehypePlugins={[rehypeRaw]}>{opt.rephrase}</Markdown>
              </label>
            ))}
          </div>
        </AlertDialogBody>

        <AlertDialogFooter style={alertDialogBodyStyles}>
          <AlertDialogCancel asChild>
            <Button
              variant="tertiary"
              onClick={() => {
              setSelectedChecks([]);
              props.onClose();
              }}
            >
              {t("core_cancel", null, analysisContextResult?.locale_code)}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant={"brand"}
              style={{ backgroundColor: theme.colors.primary }}
              onClick={() => props.onDelete(selectedChecks)}
              className="brandPrimaryBtn"
              icon={"trash"}
              disabled={loader}
            >
              {t("core_delete", null, analysisContextResult?.locale_code)} {item?.length > 1 && selectedChecks?.length >= 1 && `(${selectedChecks?.length})`}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
